import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { filterAndRank } from '../../utils/search'
import './inputs.css'

// How many matches to paint. The full list is still searched and ranked; a
// large standard can have thousands of items, and drawing all of them locks
// the menu. A specific query (every token must match) stays well under this.
const RESULT_WINDOW = 200

// Custom multiselect: selected items show as removable chips, a search box
// filters the remaining options, and picking one adds it. `value` and `onChange`
// work with arrays of the option objects. Options can be any shape via
// getOptionValue / getOptionLabel (defaults to o.value / o.label).
//
// The dropdown is rendered in a portal (fixed-positioned to the control) so it
// floats above everything and is never clipped by scrolling/overflow ancestors
// such as the modal body.
function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled,
  getOptionValue = (option) => option.value,
  getOptionLabel = (option) => option.label,
  onOpen,
  // Topic filters keep the typed text so the next pick can share a similar name.
  clearQueryOnSelect = true,
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState(null)
  const controlRef = useRef(null)
  const listRef = useRef(null)

  const updatePosition = useCallback(() => {
    const el = controlRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const margin = 32 /* keep clear of the OS taskbar at the bottom */
    const gap = 4
    const preferredMax = 195 /* ~3/4 of the previous 260 */
    const spaceBelow = window.innerHeight - rect.bottom - margin
    const spaceAbove = rect.top - margin

    // Prefer opening below; flip above when there's clearly more room there.
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow
    const maxHeight = Math.max(
      120,
      Math.min(preferredMax, openUp ? spaceAbove : spaceBelow)
    )
    const top = openUp ? rect.top - gap - maxHeight : rect.bottom + gap

    setMenuPos({ top, left: rect.left, width: rect.width, maxHeight })
  }, [])

  // Keep the portaled menu aligned to the control while open (also handles the
  // modal body scrolling, via capture-phase scroll).
  useLayoutEffect(() => {
    if (!open) return undefined
    updatePosition()
    const handler = () => updatePosition()
    window.addEventListener('resize', handler)
    window.addEventListener('scroll', handler, true)
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('scroll', handler, true)
    }
  }, [open, updatePosition])

  // Close when clicking outside the control and the (portaled) menu. Uses the
  // capture phase so it still fires inside modals, whose content stops mousedown
  // propagation (to guard their own backdrop-close) before it reaches document.
  useEffect(() => {
    const handleClick = (event) => {
      if (controlRef.current?.contains(event.target)) return
      if (listRef.current?.contains(event.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick, true)
    return () => document.removeEventListener('mousedown', handleClick, true)
  }, [])

  const selectedValues = new Set(value.map(getOptionValue))
  const unselected = options.filter((option) => !selectedValues.has(getOptionValue(option)))
  // Order-independent, ranked token search (see utils/search).
  const available = filterAndRank(unselected, query, getOptionLabel)
  const shown = available.slice(0, RESULT_WINDOW)
  const hiddenCount = available.length - shown.length

  const add = (option) => {
    onChange?.([...value, option])
    if (clearQueryOnSelect) setQuery('')
  }

  const remove = (option) => {
    onChange?.(value.filter((item) => getOptionValue(item) !== getOptionValue(option)))
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (event.key === 'Backspace' && !query && value.length) {
      remove(value[value.length - 1])
    }
  }

  const className =
    'ct-multiselect' + (open ? ' is-open' : '') + (disabled ? ' is-disabled' : '')

  return (
    <div className={className}>
      <div
        className="ct-multiselect-control"
        ref={controlRef}
        onClick={() => {
          if (disabled) return
          if (!open) onOpen?.()
          setOpen((prev) => !prev)
        }}
      >
        {value.map((option) => (
          <span className="ct-chip" key={getOptionValue(option)}>
            {getOptionLabel(option)}
            <button
              type="button"
              className="ct-chip-x"
              aria-label="Remove"
              onClick={(event) => {
                event.stopPropagation()
                remove(option)
              }}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          className="ct-multiselect-input"
          value={query}
          placeholder={value.length ? '' : placeholder}
          disabled={disabled}
          onChange={(event) => {
            setQuery(event.target.value)
            if (!open) onOpen?.()
            setOpen(true)
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      {open &&
        !disabled &&
        menuPos &&
        createPortal(
          <ul
            className="ct-multiselect-list"
            ref={listRef}
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
            }}
          >
            {shown.length === 0 ? (
              <li className="ct-multiselect-empty">No options</li>
            ) : (
              shown.map((option) => (
                <li
                  key={getOptionValue(option)}
                  className="ct-multiselect-item"
                  onMouseDown={() => add(option)}
                >
                  {getOptionLabel(option)}
                </li>
              ))
            )}
            {hiddenCount > 0 ? (
              <li className="ct-multiselect-empty">
                {hiddenCount} more — keep typing to narrow
              </li>
            ) : null}
          </ul>,
          document.body
        )}
    </div>
  )
}

export default MultiSelect
